//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useEffect, useState } from "react";

// Custom components imports (and hooks and helpers)
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { Grid, Typography, useTheme } from "@mui/material";

import { Params, useNavigate, useParams } from "react-router-dom";
import { useCreateSceneMutation, useGetScenesQuery } from "../../services/scene.service";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { IScene } from "@virtus-tech-repository/virtus-tech-repository/lib/models/scenario.model";

import { VTButton, VTTextField, VTTypography } from "@virtus-tech-repository/virtus-tech-repository";

import { LineChart, PieChart } from "@mui/x-charts";
import { ITrackingActions } from "../../models/tracking.models";
import VTOverviewUser from "../components/OverviewUser";
import { useGetUserAttemptsQuery, useGetUserInputsQuery } from "../../services/tracking.service";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VTTimelineItem from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTTimelineItem";
import { getImageForSceneOrDefaultImageIfPreviewImageDoesntExist } from "../../utils/getImageForSceneOrDefaultImageIfPreviewImageDoesntExist";
import VTUserInputsElement from "./UserInputsElement";

interface IProps {
    drawWidth: number;
}

interface userInputProp {
    sceneId: string;
    hotspotId: string;
    userData: { userName: string; answer: string }[];
}
//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Error Page, to display on breaking errors and allows home navigation
export default function VTUserInputsContainer({ drawWidth }: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##

    const theme = useTheme();
    const { scenarioId, sceneId }: Readonly<Params<string>> = useParams();

    const { data: scenes } = useGetScenesQuery(scenarioId ? scenarioId : "");

    const [currentSceneOverview, setCurrentSceneOverview] = useState<string>("");
    const { data: userInputs } = useGetUserInputsQuery({ scenarioId: scenarioId });
    const [sortedUserInputs, setSortedUserInputs] = useState<userInputProp[]>([]);

    useEffect(() => {
        if (userInputs) {
            let tempUserDataArray: userInputProp[] = [];
            if (userInputs.sceneData[0]) {
                userInputs.sceneData[0].forEach((element: any) => {
                    let tempUsername: string = element.userEmail.split("@")[0];
                    element.data.forEach((subElement: { hotspotId: string; sceneId: string; data: string }) => {
                        if (
                            !tempUserDataArray.find(
                                (findElement: userInputProp) => findElement.hotspotId === subElement.hotspotId,
                            )
                        ) {
                            tempUserDataArray.push({
                                sceneId: subElement.sceneId,
                                hotspotId: subElement.hotspotId,

                                userData: [{ userName: tempUsername, answer: subElement.data }],
                            });
                        } else {
                            let tempIndex: number = tempUserDataArray.findIndex(
                                (findElement: userInputProp) => findElement.hotspotId === subElement.hotspotId,
                            );
                            tempUserDataArray[tempIndex] = {
                                ...tempUserDataArray[tempIndex],
                                userData: [
                                    ...tempUserDataArray[tempIndex].userData,
                                    { userName: tempUsername, answer: subElement.data },
                                ],
                            };
                        }
                    });
                });
            }
            setSortedUserInputs(tempUserDataArray);
        }
    }, [userInputs]);

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Displays return button and error message
    return (
        <>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Main page content */}
            {scenes && (
                <Grid
                    sx={{
                        width: "100%",
                        height: `calc(100vh - 70px - ${drawWidth}px)`,
                        zIndex: 100000,
                        overflowY: "scroll",
                        paddingTop: "30px",
                        paddingBottom: "30px",
                        backgroundColor: theme.palette.background.default,
                    }}
                    container
                    alignContent={"flex-start"}
                    justifyContent={"space-evenly"}
                >
                    <Grid
                        container
                        sx={{
                            height: `calc(100vh - 160px - ${drawWidth}px)`,
                            backgroundColor: "#393939",
                            borderRadius: "15px",
                            overflowY: "auto",
                            width: "230px",
                        }}
                        justifyContent={"center"}
                        alignContent={"flex-start"}
                    >
                        <VTTypography
                            type="Heading3"
                            sx={{ marginLeft: "10px", marginTop: "3px", width: "100%", marginBottom: "20px" }}
                            align="left"
                        >
                            Scenes
                        </VTTypography>

                        {sortedUserInputs.length > 0 &&
                            [...scenes]
                                .sort((element1: IScene, element2: IScene) => {
                                    if (element1.order === undefined) {
                                        return 1;
                                    } else if (element2.order === undefined) {
                                        return -1;
                                    } else {
                                        return element2.order < element1.order ? 1 : -1;
                                    }
                                })
                                ?.filter((filterElement: IScene) =>
                                    sortedUserInputs.find(
                                        (findElement: userInputProp) => findElement.sceneId === filterElement.id,
                                    ),
                                )
                                .map((sceneElement: IScene) => (
                                    <Grid item xs={12} container justifyContent={"center"}>
                                        <VTTimelineItem
                                            mediaType={sceneElement.mediaType}
                                            image={getImageForSceneOrDefaultImageIfPreviewImageDoesntExist(
                                                sceneElement,
                                            )}
                                            name={sceneElement.name}
                                            disableHover
                                            active={currentSceneOverview === sceneElement.id}
                                            onClicked={() => {
                                                setCurrentSceneOverview(sceneElement.id);
                                            }}
                                            width={200}
                                            sx={{ marginBottom: "10px" }}
                                        />
                                    </Grid>
                                ))}
                    </Grid>
                    <Grid
                        container
                        sx={{
                            height: `calc(100vh - 160px - ${drawWidth}px)`,

                            borderRadius: "15px",
                            overflowY: "auto",
                            width: "calc(100% - 400px)",
                        }}
                        justifyContent={"center"}
                        alignContent={"flex-start"}
                    >
                        {sortedUserInputs
                            .filter((filterElement: userInputProp) =>
                                currentSceneOverview ? filterElement.sceneId === currentSceneOverview : true,
                            )
                            .map((mapElement: userInputProp) => {
                                return <VTUserInputsElement mapElement={mapElement} />;
                            })}
                    </Grid>
                </Grid>
            )}
        </>
    );
}
